// Copyright (C) Siemens AG 2026. All rights reserved. Confidential
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthPortalUser } from 'src/app/model/platformuser.model';
import { AppEnvironmentService } from '../app-environment.service';
import { Announcement } from 'src/app/model/announcement.model';

@Injectable({ providedIn: 'root' })
export class UserManagementService {

    private get baseApiUrl(): string { return this.appEnvService.baseApiUrl; }
    readonly contentType = 'application/json';

    constructor(private readonly http: HttpClient, private readonly appEnvService: AppEnvironmentService) {
    }

    getMe(): Observable<AuthPortalUser> {
        let accessToken;
        accessToken = localStorage.getItem('access_token');
        const addTenantUserUrl = this.baseApiUrl + `me`;

        const headers = new HttpHeaders({
            'Content-Type': this.contentType,
            'Authorization': `Bearer ${accessToken}`
        });

        const httpOptions = {
            headers: headers
        };
        return this.http.get<AuthPortalUser[]>(addTenantUserUrl, httpOptions).pipe(map(response => AuthPortalUser.FromAPI(response)));
    }

    updatePreferences(prefs: { pref_tenant_id: number }): Observable<any> {
        let accessToken = localStorage.getItem('access_token');

        const addTenantUserUrl = this.baseApiUrl + `me/prefs`;

        const headers = new HttpHeaders({
            'Content-Type': this.contentType,
            'Authorization': `Bearer ${accessToken}`
        });

        const httpOptions = {
            headers: headers
        };

        return this.http.post<Object[]>(addTenantUserUrl, prefs, httpOptions);
    }

    updateLastAnnouncementViewedDate(date: string): Observable<any> {
        let accessToken = localStorage.getItem('access_token');
        const updateDateUrl = this.baseApiUrl + `me/viewAnnouncements`;
        const headers = new HttpHeaders({
            'Content-Type': this.contentType,
            'Authorization': `Bearer ${accessToken}`
        });
        const httpOptions = {
            headers: headers
        };
        return this.http.post(updateDateUrl, { date: date }, httpOptions);
    }

    addTenantUser(userObj: any, tenantId: number): Observable<any> {
        let accessToken = localStorage.getItem('access_token');

        const addTenantUserUrl = this.baseApiUrl + `tenants/${tenantId}/users`;

        const headers = new HttpHeaders({
            'Content-Type': this.contentType,
            'Authorization': `Bearer ${accessToken}`
        });

        const httpOptions = {
            headers: headers
        };

        return this.http.post<Object[]>(addTenantUserUrl, JSON.stringify(userObj), httpOptions);
    }

    getAnnouncements(): Observable<Announcement[]> {
        let accessToken;
        accessToken = localStorage.getItem('access_token');
        const addTenantUserUrl = this.baseApiUrl + `announcements`;

        const headers = new HttpHeaders({
            'Content-Type': this.contentType,
            'Authorization': `Bearer ${accessToken}`
        });

        const httpOptions = {
            headers: headers
        };

        return this.http.get<Announcement[]>(addTenantUserUrl, httpOptions);
    }

    getAnnouncementImage(imageKey: string) {
        let accessToken = localStorage.getItem('access_token');
        const imageUrl = `${this.baseApiUrl}announcements/images?imageKey=${encodeURIComponent(imageKey)}`;

        const headers = new HttpHeaders({
            'Authorization': `Bearer ${accessToken}`
        });

        const httpOptions = {
            headers: headers,
            responseType: 'blob' as 'json'
        };

        return new Observable<string>(observer => {
            this.http.get<Blob>(imageUrl, httpOptions).subscribe({
                next: (imageBlob: Blob) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        observer.next(reader.result as string);
                        observer.complete();
                    };
                    reader.onerror = error => {
                        observer.error(error);
                        observer.complete();
                    };
                    reader.readAsDataURL(imageBlob);
                },
                error: error => {
                    console.error('Error loading image:', error);
                    observer.error(error);
                    observer.complete();
                }
            });
        });
    }
}
